import _ from 'lodash';
import { isMobileOnly } from 'react-device-detect';
import { history, matchPath } from 'umi';

import env from '@/config/env';
import { isTenantAdmin } from '@/pages/Launchpad/utils/permissions';
import { CoreEnums } from '@/utils/domain/constants';
import { isJsonLike } from '@/utils/infrastructure';
import { STORAGE_KEYS } from '@/utils/infrastructure/auth/sessionConstants';
import { getSessionItem } from '@/utils/infrastructure/auth/sessionManager';
import { Regexp } from '@/utils/infrastructure/utilities';
import { ROUTES } from './constants';
import { isUsedInFormulae, formulaBodySetup, formulaFields, fieldDependenciesMap } from './utils';

const { FormFieldBehavior, FormFieldType, FormFieldStringType } = CoreEnums;

export default class BaseFieldHandler {
  constructor(baseConfig, configData = [], dependency = true) {
    this.baseConfig = baseConfig;
    this.configData = configData;
    this.dependency = dependency;
    this.routeInfo = this.getRouteInfo();
    this.fieldState = this.getFieldState();
  }

  getRouteInfo() {
    const route = history.location?.pathname;

    return {
      route,
      math: matchPath(route, ROUTES.details),
      isHistory: matchPath(route, ROUTES.versionHistory),
      isContinue: matchPath(route, ROUTES.continue),
      isCustomList: matchPath(route, ROUTES.customList),
      isDynamicEntity: matchPath(route, ROUTES.dynamicEntity),
      isStartProcess: matchPath(route, ROUTES.startProcess),
      isPublicForm:
        matchPath(route, ROUTES.externalRequestStart) ||
        matchPath(route, ROUTES.externalRequestContinue),
    };
  }

  getFieldState() {
    const { math, isHistory } = this.routeInfo;
    const isDetails =
      (math && Regexp.isUUID(math.params.id)) || isHistory || this.baseConfig.isDetails;
    const isLocked = this.baseConfig.fieldBehavior === FormFieldBehavior.Locked;
    const isTable =
      this.baseConfig.type !== FormFieldType.Table &&
      this.baseConfig.type !== FormFieldStringType.Table;

    const user = JSON.parse(getSessionItem(STORAGE_KEYS.AUTH_USER) || '{}');
    const isUserRole = !isTenantAdmin(user?.roles);

    return {
      isDetails,
      isLocked,
      isTable,
      isUserRole,
      user,
    };
  }

  getBaseConfig() {
    const {
      id,
      type,
      internalName,
      displayText,
      displayName,
      longText,
      observations,
      value,
      inlineMode,
      defaultValue,
      locale,
      required,
      isCopyEnabled,
      generatedPublicFileURL,
      generatedDownloadFileURL,
      visibleInDetails,
      scannerEnabled,
      formula,
    } = this.baseConfig;

    const { isDetails, isLocked, isTable, isUserRole } = this.fieldState;

    const usedInFormula = !isLocked && isUsedInFormulae(internalName, this.configData);
    const hasLongText = typeof longText === 'string' && longText.trim().length > 0;

    return {
      type: 'text',
      locale: locale ?? false,
      knownType: type,
      rules: [{ required: this.dependency ? !!required : false }],
      value: value ?? defaultValue,
      isDetails,
      isLocked,

      ...(isCopyEnabled && { isCopyEnabled }),
      ...(internalName && { name: internalName, internalName }),
      ...(displayText && { label: displayText }),
      ...(displayName && { label: displayName }),
      ...(hasLongText && { label: longText, displayText: longText, isLongText: hasLongText }),
      ...(observations && !inlineMode && { help: observations }),
      ...(isDetails && isUserRole && !visibleInDetails && { hidden: true }),
      ...(!isLocked && !isDetails && scannerEnabled && { scannerEnabled }),
      ...(generatedPublicFileURL && { generatedPublicFileURL }),
      ...(generatedDownloadFileURL && { generatedDownloadFileURL }),

      ...(isLocked && {
        disabled: true,
        readOnly: true,
        styles: { pointerEvents: 'none' },
        ariaDisabled: true,
        ariaReadonly: true,
      }),

      ...(isTable && {
        ...(!isLocked && formula && !isDetails && formulaBodySetup(formula, this.configData)),
        ...(usedInFormula &&
          !isDetails && {
            isUsedInFormulae: isUsedInFormulae(internalName, this.configData),
            relatedFormulaFields: formulaFields(internalName, this.configData),
          }),
      }),
    };
  }

  getDependencies() {
    const { id } = this.baseConfig;
    const { isDetails } = this.fieldState;

    return !isDetails && this.dependency ? fieldDependenciesMap(id, this.configData) : [];
  }

  isMobile() {
    return isMobileOnly;
  }

  isJsonLike(value) {
    return isJsonLike(value);
  }

  getCommonFormRouteProps() {
    const { isContinue, isStartProcess, isPublicForm } = this.routeInfo;
    const { processId, versionId, formId } = this.baseConfig;

    return (
      (isContinue || isStartProcess || isPublicForm || processId) && {
        isContinue: !!isContinue,
        isStartProcess: !!isStartProcess,
        isPublicForm: !!isPublicForm,
        processId,
        versionId,
        formId,
      }
    );
  }

  handle() {
    throw new Error('handle() method must be implemented by field handler');
  }
}
